import { Button } from "@/components/common/Button/Button"
import { AutoFillIcon } from "@/components/common/icons/AutoFillIcon/AutoFillIcon"
import { PermissionGate } from "@/components/common/PermissionGate"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader } from "@aws-amplify/ui-react"
import { IconCheck, IconFolderOpen, IconNotes } from "@tabler/icons-react"

interface QuestionnaireHeaderActionsProps {
  continueLaterLabel: string
  selectDocumentsLabel: string
  showSelectDocumentsButton?: boolean
  selectDocumentsBadgeCount?: number
  generateAnswersLabel: string
  onContinueLater: () => void
  onSelectDocuments: () => void
  onGenerateAnswers: () => void
  isGenerateDisabled: boolean
  generateButtonElementId?: string
  showGenerateLoader?: boolean
  answeringContextLabel?: string
  showAnsweringContextButton?: boolean
  onAnsweringContext?: () => void
  /** A context is already saved for this questionnaire; shows a small check badge. */
  answeringContextSaved?: boolean
}

export const QuestionnaireHeaderActions: React.FC<
  QuestionnaireHeaderActionsProps
> = ({
  continueLaterLabel,
  selectDocumentsLabel,
  showSelectDocumentsButton = true,
  selectDocumentsBadgeCount,
  generateAnswersLabel,
  onContinueLater,
  onSelectDocuments,
  onGenerateAnswers,
  isGenerateDisabled,
  generateButtonElementId,
  showGenerateLoader = false,
  answeringContextLabel,
  showAnsweringContextButton = false,
  onAnsweringContext,
  answeringContextSaved = false,
}) => {
  const { translate } = useTranslation()
  const selectDocumentsButtonLabel =
    selectDocumentsBadgeCount && selectDocumentsBadgeCount > 0
      ? `${selectDocumentsLabel} (${selectDocumentsBadgeCount})`
      : selectDocumentsLabel

  return (
    <div className="flex items-center justify-end gap-S">
      <Button
        variantColor="tertiary"
        title={continueLaterLabel}
        label={continueLaterLabel}
        onClick={onContinueLater}
        variantSize="small"
      />
      {showAnsweringContextButton &&
      answeringContextLabel &&
      onAnsweringContext ? (
        <div className="relative">
          <Button
            variantColor="dark"
            title={answeringContextLabel}
            label={answeringContextLabel}
            onClick={onAnsweringContext}
            variantSize="small"
            IconLeft={IconNotes}
          />
          {answeringContextSaved ? (
            <span
              role="img"
              aria-label={translate("ANSWERING_CONTEXT/SAVED_BADGE")}
              title={translate("ANSWERING_CONTEXT/SAVED_BADGE")}
              className="absolute -right-1 -top-1 flex size-3.5 items-center justify-center rounded-full bg-success text-black"
            >
              <IconCheck size={10} aria-hidden="true" />
            </span>
          ) : null}
        </div>
      ) : null}
      {showSelectDocumentsButton ? (
        <Button
          variantColor="dark"
          title={selectDocumentsButtonLabel}
          label={selectDocumentsButtonLabel}
          onClick={onSelectDocuments}
          variantSize="small"
          IconLeft={IconFolderOpen}
        />
      ) : null}
      <PermissionGate permission={PERMISSIONS.QUEST_RUN}>
        <Button
          elementId={generateButtonElementId}
          variantColor="yellow"
          title={generateAnswersLabel}
          label={generateAnswersLabel}
          onClick={onGenerateAnswers}
          variantSize="small"
          IconLeft={
            showGenerateLoader
              ? () => <Loader filledColor="black" />
              : AutoFillIcon
          }
          isDisabled={isGenerateDisabled}
        />
      </PermissionGate>
    </div>
  )
}
